import { InjectionToken } from '@angular/core';
import type { RegisterOutcome, SyncResult, UploadResult } from '../entities/upload.models';

// Integrity metadata (size/mime/hash) is server-derived at register time —
// the browser sends only identity + the display name.
export interface RegisterPhotoInput {
  fileId: string;
  sceneId?: string;
  originalName?: string;
  // Дата файла на диске (ISO) — запасной ориентир сортировки, когда в кадре нет EXIF.
  fileModifiedAt?: string;
}

export type SharpeningLevel = 'none' | 'low' | 'optimal' | 'high';

export interface EnsureProjectInfo {
  lumirProjectId: string;
  faceRecognitionEnabled: boolean;
  // Account-level «Принимать RAW» flag (spec 15 §13 F5) — the store fails RAW
  // entries with 'raw_disabled' when off, before any bytes leave the browser.
  rawUploadEnabled: boolean;
  // Account-level web-preview sharpening (spec 02 §5) — forwarded per upload in
  // the process-metadata header; the transform fleet applies it to previews.
  sharpeningLevel: SharpeningLevel;
}

export interface UploadOpts {
  notGenerateNewPersons?: boolean;
  sharpening?: SharpeningLevel;
  // Bytes actually on the wire for THIS file, 0–100 — the upload bar weighs the
  // batch's files by size to turn these into one honest percent.
  onProgress?: (percent: number) => void;
}

// The register endpoint hard-caps one call at this many photos (ArrayMaxSize on the
// server DTO); every caller slices its batch by it, so the number lives once, here.
export const REGISTER_BATCH_LIMIT = 200;

export interface GalleryUploadDataPort {
  ensureProject(galleryId: string): Promise<EnsureProjectInfo | null>;
  upload(projectId: string, file: File, fileId: string, opts?: UploadOpts): Promise<UploadResult>;
  register(galleryId: string, photos: RegisterPhotoInput[]): Promise<RegisterOutcome[] | null>;
  syncStatus(galleryId: string): Promise<SyncResult | null>;
  /** Aborts what the project has on the wire: the batch was cancelled. */
  cancelUploads(projectId: string): void;
}

export const GALLERY_UPLOAD_DATA = new InjectionToken<GalleryUploadDataPort>('GALLERY_UPLOAD_DATA');

// Every file uploads and registers instantly; readiness flips on the second poll pass.
export class MockGalleryUploadData implements GalleryUploadDataPort {
  private readonly pollCounts = new Map<string, number>();

  async ensureProject(galleryId: string): Promise<EnsureProjectInfo | null> {
    return galleryId
      ? { lumirProjectId: 'mock-project-id', faceRecognitionEnabled: false, rawUploadEnabled: false, sharpeningLevel: 'optimal' }
      : null;
  }

  async upload(_projectId?: string, _file?: File, fileId?: string): Promise<UploadResult> {
    return { ok: true, fileUrl: null, ...(fileId ? { fileId } : {}) };
  }

  async register(_galleryId: string, photos: RegisterPhotoInput[]): Promise<RegisterOutcome[] | null> {
    this.registered.push(...photos);
    return photos.map(photo => ({ fileId: photo.fileId, status: 'created', photoId: `photo-${photo.fileId}` }));
  }

  async syncStatus(galleryId: string): Promise<SyncResult | null> {
    const passes = (this.pollCounts.get(galleryId) ?? 0) + 1;
    this.pollCounts.set(galleryId, passes);
    const status = passes >= 2 ? 'ready' : 'pending';
    return {
      photos: Object.fromEntries(this.registered.map(photo => [`photo-${photo.fileId}`, { status }])),
    };
  }

  cancelUploads(): void {
    return;
  }

  readonly registered: RegisterPhotoInput[] = [];
}
